import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../../../components/Component";
import http from "../../../utils/http-common";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { isAbortError } from "../../../utils/asyncRequestGuards";

/** Stable default — avoid `filterCandidate = () => true` in destructuring (new function every render → effect loop). */
function defaultFilterCandidate() {
  return true;
}

function displayName(u) {
  if (!u) return "";
  const parts = [u.salutation, u.first_name, u.last_name].filter((p) => p != null && String(p).trim() !== "");
  const line = parts.map((p) => String(p).trim()).join(" ");
  return line || u.email || u.username || "";
}

/**
 * Admin: search users by email or name (GET /api/v1/user?search=…), pick one row.
 */
const UserPicker = ({
  idPrefix = "user-picker",
  value,
  onChange,
  disabled = false,
  excludeUserIds,
  filterCandidate = defaultFilterCandidate,
  placeholder = "Search…",
  emptyHint = "No users found.",
  onRequestCreateAccount,
}) => {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 320);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const excludeIdsKey =
    excludeUserIds && excludeUserIds.length > 0
      ? [...excludeUserIds].map((id) => String(id)).sort().join("\u0001")
      : "";

  const excludeSet = useMemo(() => {
    if (!excludeIdsKey) return new Set();
    return new Set(excludeIdsKey.split("\u0001"));
  }, [excludeIdsKey]);

  const filterCandidateRef = useRef(filterCandidate);
  filterCandidateRef.current = filterCandidate;

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!debounced || String(debounced).trim().length < 2) {
      setOptions((prev) => (prev.length === 0 ? prev : []));
      setLoading((prev) => (prev === false ? prev : false));
      return undefined;
    }
    const ac = new AbortController();
    setLoading(true);
    http
      .get("/api/v1/user", {
        params: { search: debounced.trim(), limit: 25 },
        signal: ac.signal,
      })
      .then((res) => {
        const raw = res.data?.data || [];
        const list = Array.isArray(raw) ? raw : [];
        const filtered = list.filter((u) => {
          if (!u?._id) return false;
          if (excludeSet.has(String(u._id))) return false;
          try {
            return filterCandidateRef.current(u);
          } catch {
            return false;
          }
        });
        setOptions(filtered);
      })
      .catch((e) => {
        if (!isAbortError(e)) setOptions([]);
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [debounced, excludeSet]);

  const pick = (u) => {
    onChange(u);
    setSearch("");
    setOptions([]);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setSearch("");
    setOptions([]);
  };

  const inputId = `${idPrefix}-input`;

  if (value && value._id) {
    return (
      <div className="d-flex flex-wrap align-items-center flex-g-2">
        <div className="form-control-plaintext border rounded px-2 py-1 flex-grow-1 min-w-0">
          <strong className="d-block text-truncate">{displayName(value)}</strong>
          <span className="small text-soft text-truncate d-block">{value.email || "—"}</span>
        </div>
        <Button type="button" color="light" size="sm" disabled={disabled} onClick={clear}>
          Clear
        </Button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="position-relative">
      <input
        id={inputId}
        type="search"
        className="form-control"
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${idPrefix}-listbox`}
      />
      {open && (loading || options.length > 0 || (debounced && String(debounced).trim().length >= 2)) ? (
        <ul
          id={`${idPrefix}-listbox`}
          role="listbox"
          className="dropdown-menu show w-100 position-absolute shadow-sm mt-1"
          style={{ maxHeight: 280, overflowY: "auto", zIndex: 1050 }}
        >
          {loading ? (
            <li className="dropdown-item text-soft small">Searching…</li>
          ) : options.length === 0 ? (
            <li className="dropdown-item text-soft small">
              {emptyHint}
              {typeof onRequestCreateAccount === "function" ? (
                <div className="mt-2">
                  <Button type="button" color="primary" size="sm" outline onClick={() => onRequestCreateAccount()}>
                    Create account
                  </Button>
                </div>
              ) : null}
            </li>
          ) : (
            options.map((u) => (
              <li key={u._id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="dropdown-item text-start"
                  onClick={() => pick(u)}
                >
                  <span className="d-block fw-medium">{displayName(u) || u.email}</span>
                  <span className="small text-soft">{u.email}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default UserPicker;
